import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Check, MoreHorizontal, Pencil, Plus, Search, Sparkles, X } from 'lucide-react';
import { apiClient } from '../api';
import orgApi from '../api/organizations';
import { useAuth } from '../auth/AuthProvider';
import { useAssistantRecord, useChat } from '../contexts/ChatContext';
import { errMessage, monoLabel, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle } from '../components/UI/ink';
import { parseTs } from '../components/SecurityAlerts/model';
import CloseCaseDialog from '../components/Cases/CloseCaseDialog';
import { PriorityChip } from './Cases';
import { useVerdicts } from '../hooks/useVerdicts';
import VerdictBadge from '../components/Triage/VerdictBadge';
import {
  KC_SHORT, NEXT, OUTCOME_LABEL, PRIORITIES, STATUS_LABEL, SYSTEM,
  age, caseRef, furthestStage, initials, isOverdue, kcSentence, stamp,
} from '../components/Cases/model';

// One case: progress, how far the attack got, one evidence list (with
// suggestions from the same hosts), one activity feed with the note box on
// top, and owner / assets / response / similar cases on the side.

const fieldStyle = { background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' };
const sevTok = (s) => ({ critical: 'crit', high: 'high', medium: 'med', low: 'low' }[String(s || '').toLowerCase()] || null);
const SEV_SHORT = { crit: 'CRIT', high: 'HIGH', med: 'MED', low: 'LOW' };
const Card = ({ children, className = '' }) => <section className={`rounded-[14px] border ${className}`} style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>{children}</section>;
const H2 = ({ children, right }) => <div className="flex items-baseline justify-between gap-3"><h2 className="m-0 text-[15px] font-semibold">{children}</h2>{right}</div>;
const Chip = ({ on, onClick, children }) => (
  <button type="button" onClick={onClick} aria-pressed={on} className="h-8 px-2.5 rounded-lg border text-[12.5px]" style={{ background: on ? 'var(--surf-raised)' : 'transparent', borderColor: on ? 'var(--accent)' : 'var(--surf-line)' }}>{children}</button>
);
const Tag = ({ kind, sev }) => {
  const k = sevTok(sev);
  return (
    <span className="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-md font-mono text-[10px] font-medium tracking-[0.06em] whitespace-nowrap" style={{ background: k ? `var(--sev-${k}-bg)` : 'var(--surf-raised)' }}>
      <span className="w-1.5 h-1.5 rounded-[1px] box-border" style={{ background: k ? `var(--sev-${k}-fg)` : 'transparent', border: k ? 'none' : '1px solid var(--ink-faint)' }} />{kind}{k ? ` · ${SEV_SHORT[k]}` : ''}
    </span>
  );
};

// Link evidence: pick a finding, IoC alert or CVE alert from this organisation.
function EvidencePicker({ kase, onLinked, onClose }) {
  const [type, setType] = useState('finding');
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const inCase = useMemo(() => new Set([
    ...(kase.findings || []).map(f => `finding:${f.finding_id}`),
    ...(kase.alerts || []).map(a => `${a.alert_type}:${a.alert_id}`),
  ]), [kase]);
  useEffect(() => {
    setItems(null);
    const url = type === 'finding' ? '/api/findings?status=new,triaged,investigating,contained&limit=200' : `/api/alerts/${type}?resolved=0`;
    apiClient.get(url).then(r => {
      const list = type === 'finding' ? r.data?.findings || [] : type === 'ioc' ? r.data?.ioc_alerts || [] : r.data?.cve_alerts || [];
      setItems(list.map(x => type === 'finding'
        ? { id: x.id, t: x.title, m: `${x.id} · ${x.server_hostname || ''} · ${x.severity}` }
        : type === 'ioc' ? { id: x.id, t: `${x.ioc_type} ${x.ioc_value}`, m: `IoC alert ${x.id} · ${x.threat_level || ''}` }
          : { id: x.id, t: `${x.cve_id} · ${x.package_name}`, m: `CVE alert ${x.id} · ${x.severity}` }));
    }).catch(() => setItems([]));
  }, [type]);
  const link = async (id) => {
    setBusy(id); setError(null);
    try {
      if (type === 'finding') await apiClient.post(`/api/cases/${kase.id}/findings`, { finding_id: id });
      else await apiClient.post(`/api/cases/${kase.id}/alerts`, { alert_type: type, alert_id: id });
      onLinked();
    } catch (err) {
      setError(errMessage(err, 'Could not link it.'));
    } finally {
      setBusy(null);
    }
  };
  const needle = q.trim().toLowerCase();
  const shown = (items || []).filter(i => !inCase.has(`${type}:${i.id}`)).filter(i => !needle || `${i.t} ${i.m}`.toLowerCase().includes(needle)).slice(0, 30);
  return (
    <div className="flex flex-col gap-2 px-4 py-3 border-b" style={{ borderColor: 'var(--surf-line)', background: 'var(--surf-bg)' }}>
      <div className="flex flex-wrap gap-2">
        <label htmlFor="ev-type" className="sr-only">Type</label>
        <select id="ev-type" value={type} onChange={e => setType(e.target.value)} className="h-9 px-2 rounded-lg border text-[13px]" style={fieldStyle}><option value="finding">Finding</option><option value="ioc">IoC alert</option><option value="cve">CVE alert</option></select>
        <div className="flex-grow min-w-[160px] h-9 box-border flex items-center gap-2 px-2.5 rounded-lg border" style={fieldStyle}>
          <Search className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--ink-faint)' }} />
          <input autoFocus aria-label="Search evidence" value={q} onChange={e => setQ(e.target.value)} placeholder="Search open items" className="flex-grow min-w-0 bg-transparent border-0 outline-none text-[13px]" style={{ color: 'var(--ink-text)' }} />
        </div>
        <button type="button" onClick={onClose} className="h-9 px-2.5 text-[13px]" style={{ color: 'var(--ink-muted)' }}>Done</button>
      </div>
      {error && <span role="alert" className="text-xs" style={{ color: 'var(--sev-crit-fg)' }}>{error}</span>}
      <div className="max-h-[260px] overflow-y-auto flex flex-col">
        {items === null && <span className="py-2 text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>Loading…</span>}
        {items && !shown.length && <span className="py-2 text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>Nothing open to link.</span>}
        {shown.map(i => (
          <div key={i.id} className="flex items-center gap-2.5 min-h-10 py-1">
            <span className="flex-grow min-w-0 flex flex-col"><span className="text-[12.5px] truncate">{i.t}</span><span className="font-mono text-[10.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{i.m}</span></span>
            <button type="button" disabled={busy === i.id} onClick={() => link(i.id)} className="h-8 shrink-0 px-3 rounded-lg border text-[12.5px] disabled:opacity-50" style={{ borderColor: 'var(--surf-line-strong)' }}>{busy === i.id ? '…' : 'Link'}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CaseDetail() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user, permissions = [] } = useAuth();
  const canRate = permissions.includes('events:update');
  const me = user?.email || user?.sub || '';
  const { setOpen: openAssistant, send } = useChat();
  const [kase, setKase] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [members, setMembers] = useState([]);
  const [risk, setRisk] = useState({});
  const [suggest, setSuggest] = useState([]);
  const [picking, setPicking] = useState(false);
  const [actF, setActF] = useState('all');
  const [note, setNote] = useState('');
  const [editing, setEditing] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState('');
  const [closing, setClosing] = useState(false);
  const [menu, setMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const menuRef = useRef(null);

  useAssistantRecord(kase ? { kind: 'case', id: kase.id, label: kase.title } : null);

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get(`/api/cases/${caseId}`);
      setKase(r.data.case);
      setTitle(r.data.case.title);
      setError(null);
    } catch (err) {
      setError(err.response?.status === 404 ? 'not-found' : errMessage(err, 'Could not load this case.'));
    }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    orgApi.getMembers().then(m => setMembers((m?.members || m?.users || []).filter(x => x.email && (!x.status || x.status === 'active')))).catch(() => {});
    apiClient.get('/api/dashboard/risk').then(r => setRisk(Object.fromEntries((r.data?.servers || []).map(s => [s.id, s])))).catch(() => {});
  }, []);
  useEffect(() => {
    if (!menu) return undefined;
    const close = (e) => { if (!menuRef.current?.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menu]);

  // Open items on the same hosts that aren't in the case yet.
  useEffect(() => {
    if (!kase || kase.status === 'closed') { setSuggest([]); return; }
    const hosts = (kase.hosts || []).slice(0, 5);
    if (!hosts.length) { setSuggest([]); return; }
    const inCase = new Set([...(kase.findings || []).map(f => `f:${f.finding_id}`), ...(kase.alerts || []).map(a => `${a.alert_type}:${a.alert_id}`)]);
    Promise.all(hosts.map(h => Promise.allSettled([
      apiClient.get(`/api/findings?server_id=${encodeURIComponent(h.id)}&status=new,triaged,investigating,contained&limit=20`),
      apiClient.get(`/api/alerts/ioc?server_id=${encodeURIComponent(h.id)}&resolved=0`),
    ]).then(([f, i]) => [
      ...(f.status === 'fulfilled' ? f.value.data?.findings || [] : []).filter(x => !inCase.has(`f:${x.id}`)).map(x => ({ key: `f:${x.id}`, type: 'finding', id: x.id, kind: 'FINDING', sev: x.severity, t: x.title, h: h.hostname })),
      ...(i.status === 'fulfilled' ? i.value.data?.ioc_alerts || [] : []).filter(x => !inCase.has(`ioc:${x.id}`)).map(x => ({ key: `ioc:${x.id}`, type: 'ioc', id: x.id, kind: 'IOC', sev: x.threat_level, t: `${x.ioc_type} ${x.ioc_value}`, h: h.hostname })),
    ]))).then(lists => setSuggest(lists.flat().slice(0, 5))).catch(() => setSuggest([]));
  }, [kase]);

  const nameOf = useCallback((email) => {
    if (!email) return 'Unassigned';
    if (email === SYSTEM) return 'DragonEye';
    return members.find(m => m.email?.toLowerCase() === String(email).toLowerCase())?.full_name || email;
  }, [members]);

  const act = async (fn, okText) => {
    setBusy(true); setNotice(null);
    try {
      await fn();
      if (okText) setNotice({ kind: 'ok', text: okText });
      await load();
      return true;
    } catch (err) {
      setNotice({ kind: 'error', text: errMessage(err, 'That didn’t work.') });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const patch = (body, okText) => act(() => apiClient.patch(`/api/cases/${caseId}`, body), okText);

  // Derived from kase directly: the early returns below come before `evidence` is built.
  const verdictKeys = useMemo(
    () => [
      ...((kase && kase.findings) || []).map(f => `finding:${f.finding_id}`),
      ...((kase && kase.alerts) || []).map(a => `${a.alert_type}:${a.alert_id}`),
    ],
    [kase],
  );
  const { verdicts, setVerdict } = useVerdicts(verdictKeys);

  if (error === 'not-found') {
    return (
      <div className="p-6 lg:p-8 flex flex-col gap-3" style={{ color: 'var(--ink-text)' }}>
        <h1 className="m-0 font-heading text-2xl font-semibold">Case not found</h1>
        <Link to="/cases" className="text-sm" style={{ color: 'var(--ink-link)' }}>Back to Cases</Link>
      </div>
    );
  }
  if (error) return <div className="p-8 text-sm" role="alert" style={{ color: 'var(--sev-crit-fg)' }}>{error}</div>;
  if (!kase) return <div className="p-8 text-sm" style={{ color: 'var(--ink-faint)' }}>Loading case…</div>;

  const closed = kase.status === 'closed';
  const next = NEXT[kase.status];
  const auto = kase.created_by === SYSTEM;
  const firstAlert = (kase.alerts || [])[0];
  const kicker = `${caseRef(kase.id)} · ${auto ? `AUTO-CREATED${firstAlert ? ` FROM ${firstAlert.alert_type.toUpperCase()} ALERT ${firstAlert.alert_id}` : ''}` : `OPENED BY ${nameOf(kase.created_by).toUpperCase()}`}`;

  // Progress: when each status was reached, from the activity log.
  const reached = { open: kase.created_at };
  (kase.timeline || []).forEach(e => {
    const m = e.kind === 'event' && e.event_type === 'status_changed' && /to (\w+)$/.exec(e.detail || '');
    if (m && !reached[m[1]]) reached[m[1]] = e.created_at;
    if (e.event_type === 'reopened') { delete reached.closed; }
  });
  if (closed) reached.closed = kase.closed_at;
  const order = ['open', 'investigating', 'remediation', 'closed'];
  const idx = order.indexOf(kase.status);

  const kc = kase.kill_chain || [];
  const last = furthestStage(kc);

  const evidence = [
    ...(kase.findings || []).map(f => ({
      key: `f:${f.finding_id}`, vkey: `finding:${f.finding_id}`, kind: 'FINDING', sev: f.severity, t: f.title || f.finding_id, to: `/findings?finding=${f.finding_id}`,
      m: [f.finding_id, f.server_hostname && `${f.server_hostname}${Number(f.asset_deleted) ? ' (asset deleted)' : ''}`, f.mitre_techniques && (() => { try { return JSON.parse(f.mitre_techniques).join(', '); } catch { return null; } })()].filter(Boolean).join(' · '),
      st: [f.status && f.status[0].toUpperCase() + f.status.slice(1), f.assigned_to && f.assigned_to !== 'null' ? nameOf(f.assigned_to) : null].filter(Boolean).join(' · '),
      unlink: () => act(() => apiClient.delete(`/api/cases/${caseId}/findings/${f.finding_id}`), 'Finding unlinked.'),
    })),
    ...(kase.alerts || []).map(a => ({
      key: `${a.alert_type}:${a.alert_id}`, vkey: `${a.alert_type}:${a.alert_id}`, kind: a.alert_type.toUpperCase(), sev: a.severity, to: `/security-alerts?alert=${a.alert_type}-${a.alert_id}`,
      t: a.alert_type === 'ioc' ? (a.ioc_value ? `${a.ioc_type} ${a.ioc_value}` : `IoC alert ${a.alert_id} (no longer available)`) : (a.cve_id ? `${a.cve_id}${a.package_name ? ` · ${a.package_name} ${a.version || ''}` : ''}` : `CVE alert ${a.alert_id} (no longer available)`),
      m: [`${a.alert_type.toUpperCase()} alert ${a.alert_id}`, a.server_hostname && `${a.server_hostname}${Number(a.asset_deleted) ? ' (asset deleted)' : ''}`, a.attack_technique, a.cvss_score != null ? `CVSS ${a.cvss_score}` : null].filter(Boolean).join(' · '),
      st: a.resolved ? 'Resolved' : 'Active',
      unlink: () => act(() => apiClient.delete(`/api/cases/${caseId}/alerts/${a.alert_type}/${a.alert_id}`), 'Alert unlinked.'),
    })),
  ].sort((x, y) => ({ crit: 0, high: 1, med: 2, low: 3 }[sevTok(x.sev)] ?? 4) - ({ crit: 0, high: 1, med: 2, low: 3 }[sevTok(y.sev)] ?? 4));

  const feed = (kase.timeline || []).slice().reverse().filter(e => actF === 'all' || (actF === 'notes' ? e.kind === 'note' : e.kind === 'event'));
  const dot = (e) => (e.kind === 'note' ? 'var(--accent)' : e.event_type === 'closed' ? 'var(--sev-good-fg)' : /linked/.test(e.event_type) ? 'var(--sev-high-fg)' : 'var(--ink-muted)');
  const waitingRun = (kase.runs || []).find(r => r.status === 'waiting_approval');

  const facts = [
    ['Opened', `${stamp(kase.created_at)} by ${nameOf(kase.created_by)}`],
    ['Updated', stamp(kase.updated_at)],
    closed ? ['Closed', `${stamp(kase.closed_at)}${kase.resolution_outcome ? ` · ${OUTCOME_LABEL[kase.resolution_outcome]}` : ''}`] : null,
    ['Case ID', caseRef(kase.id)],
  ].filter(Boolean);

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-4" style={{ color: 'var(--ink-text)' }}>
      <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--ink-faint)' }}>
        <Link to={`/cases?case=${kase.id}`} style={{ color: 'var(--ink-link)' }}>Cases</Link><span aria-hidden="true">/</span><span className="font-mono text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>{caseRef(kase.id)}</span>
      </nav>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2 min-w-0">
          <span className="font-mono text-[11.5px] tracking-[0.14em] break-words" style={{ color: 'var(--ink-link)' }}>{kicker}</span>
          {renaming ? (
            <form onSubmit={async e => { e.preventDefault(); if (title.trim() && title.trim() !== kase.title) { if (await patch({ title: title.trim() })) setRenaming(false); } else setRenaming(false); }} className="flex items-center gap-2">
              <label htmlFor="case-title" className="sr-only">Case title</label>
              <input id="case-title" autoFocus value={title} onChange={e => setTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Escape') { setRenaming(false); setTitle(kase.title); } }} className="h-11 box-border px-3 rounded-[10px] border font-heading text-xl font-semibold w-[min(560px,75vw)]" style={fieldStyle} />
              <button type="submit" aria-label="Save title" className="w-10 h-10 flex items-center justify-center rounded-lg" style={primaryBtnStyle}><Check className="w-4 h-4" /></button>
              <button type="button" aria-label="Cancel" onClick={() => { setRenaming(false); setTitle(kase.title); }} className="w-10 h-10 flex items-center justify-center rounded-lg border" style={{ borderColor: 'var(--surf-line)' }}><X className="w-4 h-4" /></button>
            </form>
          ) : (
            <span className="flex items-center gap-2 min-w-0">
              <h1 className="m-0 font-heading text-[30px] font-semibold tracking-[-0.01em] leading-tight break-words">{kase.title}</h1>
              <button type="button" onClick={() => setRenaming(true)} aria-label="Rename case" className="w-[34px] h-[34px] shrink-0 flex items-center justify-center rounded-lg" style={{ color: 'var(--ink-faint)' }}><Pencil className="w-[15px] h-[15px]" strokeWidth={1.9} /></button>
            </span>
          )}
          <span className="flex flex-wrap items-center gap-2.5 text-[13.5px]" style={{ color: 'var(--ink-muted)' }}>
            <PriorityChip p={kase.priority} />
            {closed ? `Closed${kase.resolution_outcome ? ` · ${OUTCOME_LABEL[kase.resolution_outcome]}` : ''}` : STATUS_LABEL[kase.status]} · {nameOf(kase.assigned_to)} · opened {stamp(kase.created_at)}{closed ? '' : ` · open ${age(kase.created_at)}`}
            {isOverdue(kase) && <span className="h-5 inline-flex items-center px-1.5 rounded-[5px] font-mono text-[10px] tracking-[0.06em]" style={{ background: 'var(--sev-high-bg)' }}>OVERDUE</span>}
          </span>
        </div>
        <div ref={menuRef} className="relative flex flex-wrap items-center gap-2.5">
          <button type="button" onClick={() => { openAssistant(true); send('/summarise this case'); }} className={secondaryBtn} style={secondaryBtnStyle}><Sparkles className="w-4 h-4" strokeWidth={1.9} style={{ color: 'var(--accent)' }} />Draft summary</button>
          {closed ? (
            <button type="button" disabled={busy} onClick={() => act(() => apiClient.post(`/api/cases/${caseId}/reopen`), 'Case reopened.')} className={primaryBtn} style={primaryBtnStyle}>Reopen case</button>
          ) : (
            <>
              <button type="button" onClick={() => setClosing(true)} className={secondaryBtn} style={secondaryBtnStyle}>Close case…</button>
              {next && <button type="button" disabled={busy} onClick={() => patch({ status: next[0] }, `Moved to ${STATUS_LABEL[next[0]]}.`)} className={primaryBtn} style={primaryBtnStyle}>{next[1]}</button>}
            </>
          )}
          <button type="button" onClick={() => setMenu(v => !v)} aria-label="More actions" aria-haspopup="menu" aria-expanded={menu} className="w-11 h-11 flex items-center justify-center rounded-[10px] border" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-muted)' }}><MoreHorizontal className="w-4 h-4" /></button>
          {menu && (
            <div role="menu" className="absolute right-0 top-[52px] z-20 w-56 flex flex-col p-1.5 rounded-[10px] border" style={{ background: 'var(--surf-raised)', borderColor: 'var(--surf-line-strong)', boxShadow: '0 16px 40px rgba(0,0,0,0.35)' }}>
              <button type="button" role="menuitem" onClick={() => { setMenu(false); setRenaming(true); }} className="min-h-10 px-2.5 rounded-[7px] text-left text-[13.5px] hover:bg-[color:var(--surf-surface)]">Rename</button>
              <button type="button" role="menuitem" onClick={() => { setMenu(false); setConfirmDelete(true); }} className="min-h-10 px-2.5 rounded-[7px] text-left text-[13.5px] hover:bg-[color:var(--surf-surface)]" style={{ color: 'var(--sev-crit-fg)' }}>Delete case…</button>
            </div>
          )}
        </div>
      </div>

      {notice && (
        <div role={notice.kind === 'error' ? 'alert' : 'status'} className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-[10px] border text-[13px]" style={notice.kind === 'error' ? { background: 'var(--sev-crit-bg)', borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)' } : { background: 'var(--sev-good-bg)', borderColor: 'var(--sev-good-fg)' }}>
          <span>{notice.text}</span><button type="button" onClick={() => setNotice(null)} aria-label="Dismiss"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      {confirmDelete && (
        <div role="dialog" aria-label="Confirm delete" className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl border" style={{ background: 'var(--surf-raised)', borderColor: 'var(--sev-crit-fg)' }}>
          <span className="flex-[1_1_320px] text-[13px] leading-normal"><strong>Delete {caseRef(kase.id)}?</strong> <span style={{ color: 'var(--ink-muted)' }}>Its notes and activity go. The findings and alerts it links stay. Closing it keeps the record instead.</span></span>
          <button type="button" disabled={busy} onClick={async () => { if (await act(() => apiClient.delete(`/api/cases/${caseId}`))) navigate('/cases'); }} className="h-[38px] px-3.5 rounded-[9px] text-[13.5px] font-semibold" style={{ background: 'var(--sev-crit-fg)', color: '#0A0A0A' }}>Delete case</button>
          <button type="button" onClick={() => setConfirmDelete(false)} className="h-[38px] px-2.5 text-[13.5px]" style={{ color: 'var(--ink-muted)' }}>Cancel</button>
        </div>
      )}
      {closed && kase.resolution_summary && (
        <div className="flex flex-col gap-1 px-4 py-3 rounded-[10px]" style={{ background: 'var(--surf-raised)' }}>
          <span className="font-mono text-[10.5px] tracking-[0.12em]">CLOSED {stamp(kase.closed_at).toUpperCase()}{kase.resolution_outcome ? ` · ${OUTCOME_LABEL[kase.resolution_outcome].toUpperCase()}` : ''}</span>
          <span className="text-[13.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{kase.resolution_summary}</span>
        </div>
      )}

      {/* Progress and kill chain */}
      <div className="grid gap-2.5 grid-cols-[repeat(auto-fit,minmax(300px,1fr))]">
        <Card className="flex flex-col gap-2.5 px-4 py-3.5">
          <span className={monoLabel} style={{ color: 'var(--ink-faint)' }}>PROGRESS</span>
          <div className="grid grid-cols-4 gap-1">
            {order.map((s, i) => (
              <span key={s} className="flex flex-col gap-[5px] min-w-0">
                <span className="h-1 rounded-sm" style={{ background: i < idx ? 'var(--ink-muted)' : i === idx ? 'var(--accent)' : 'var(--surf-line)' }} />
                <span className="text-xs truncate" style={{ fontWeight: i === idx ? 600 : 400, color: i === idx ? 'var(--ink-text)' : i < idx ? 'var(--ink-muted)' : 'var(--ink-faint)' }}>{STATUS_LABEL[s]}</span>
                <span className="font-mono text-[10.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{i <= idx && reached[s] ? stamp(reached[s]).replace('today ', '') : ''}</span>
              </span>
            ))}
          </div>
        </Card>
        <Card className="flex flex-col gap-2.5 px-4 py-3.5">
          <span className="flex justify-between gap-3"><span className={monoLabel} style={{ color: 'var(--ink-faint)' }}>HOW FAR IT GOT · KILL CHAIN</span><span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{last >= 0 ? `furthest: ${KC_SHORT[last]}` : 'no tagged evidence'}</span></span>
          <div className="grid grid-cols-7 gap-[3px]">
            {KC_SHORT.map((s, i) => (
              <span key={s} className="flex flex-col gap-[5px] min-w-0">
                <span className="h-1.5 rounded-sm" style={{ background: kc[i] ? (i === last ? 'var(--sev-crit-fg)' : 'var(--sev-high-fg)') : 'var(--surf-line)' }} />
                <span className="text-[10.5px] truncate" style={{ color: kc[i] ? 'var(--ink-text)' : 'var(--ink-faint)', fontWeight: i === last ? 600 : 400 }}>{s}</span>
                <span className="font-mono text-[10px]" style={{ color: 'var(--ink-faint)' }}>{kc[i] || ''}</span>
              </span>
            ))}
          </div>
          <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{kcSentence(kc)}</span>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-5 items-start">
        <div className="flex flex-col gap-[18px] min-w-0">
          {/* Evidence */}
          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-center gap-2.5 px-4 py-3.5 border-b" style={{ borderColor: 'var(--surf-line)' }}>
              <h2 className="m-0 text-[15px] font-semibold">Evidence</h2>
              <span className="font-mono text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{(kase.findings || []).length} finding{(kase.findings || []).length === 1 ? '' : 's'} · {(kase.alerts || []).length} alert{(kase.alerts || []).length === 1 ? '' : 's'}</span>
              {!closed && <button type="button" onClick={() => setPicking(v => !v)} aria-expanded={picking} className="ml-auto h-[34px] flex items-center gap-1.5 px-3 rounded-lg border text-[13px]" style={{ borderColor: 'var(--surf-line-strong)' }}><Plus className="w-[13px] h-[13px]" strokeWidth={2.2} />Link evidence</button>}
            </div>
            {picking && <EvidencePicker kase={kase} onLinked={() => { load(); }} onClose={() => setPicking(false)} />}
            {!evidence.length && <div className="px-4 py-8 text-center text-[13px]" style={{ color: 'var(--ink-faint)' }}>Nothing linked yet. Link the findings and alerts this case is about.</div>}
            {evidence.map(e => (
              <div key={e.key} className="flex items-center gap-3 min-h-[62px] box-border py-2 pl-4 pr-2.5 border-b" style={{ borderColor: 'var(--surf-line)' }}>
                <span className="w-[110px] shrink-0"><Tag kind={e.kind} sev={e.sev} /></span>
                <Link to={e.to} className="flex-grow min-w-0 flex flex-col gap-1 hover:underline" style={{ color: 'var(--ink-text)' }}>
                  <span className="text-[13.5px] font-medium truncate">{e.t}</span>
                  <span className="font-mono text-[11px] truncate" style={{ color: 'var(--ink-faint)' }}>{e.m}</span>
                </Link>
                <span className="shrink-0 max-w-[40%]"><VerdictBadge verdict={verdicts[e.vkey]} interactive canRate={canRate} onChange={(v) => setVerdict(e.vkey, v)} /></span>
                <span className="shrink-0 hidden sm:block text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>{e.st}</span>
                {!closed && <button type="button" disabled={busy} onClick={e.unlink} aria-label={`Unlink ${e.t}`} className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg" style={{ color: 'var(--ink-faint)' }}><X className="w-3.5 h-3.5" strokeWidth={2} /></button>}
              </div>
            ))}
            {suggest.length > 0 && (
              <div className="flex flex-col gap-1.5 px-4 pt-3 pb-3.5" style={{ background: 'var(--surf-bg)' }}>
                <span className="font-mono text-[10px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>ALSO OPEN ON THESE HOSTS · NOT IN THIS CASE</span>
                {suggest.map(g => (
                  <div key={g.key} className="flex items-center gap-2.5 min-h-10">
                    <Tag kind={g.kind} sev={g.sev} />
                    <span className="flex-grow min-w-0 text-[12.5px] truncate">{g.t} <span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>· {g.h}</span></span>
                    <button type="button" disabled={busy} onClick={() => act(() => (g.type === 'finding' ? apiClient.post(`/api/cases/${caseId}/findings`, { finding_id: g.id }) : apiClient.post(`/api/cases/${caseId}/alerts`, { alert_type: g.type, alert_id: g.id })), 'Linked.')} className="h-8 shrink-0 px-3 rounded-lg border text-[12.5px]" style={{ borderColor: 'var(--surf-line-strong)' }}>Link</button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Activity */}
          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-center gap-2 px-4 py-3.5 border-b" style={{ borderColor: 'var(--surf-line)' }}>
              <h2 className="m-0 mr-2 text-[15px] font-semibold">Activity</h2>
              {[['all', 'All'], ['notes', 'Notes'], ['events', 'Changes']].map(([k, t]) => <Chip key={k} on={actF === k} onClick={() => setActF(k)}>{t}</Chip>)}
            </div>
            <form onSubmit={async e => { e.preventDefault(); if (note.trim() && await act(() => apiClient.post(`/api/cases/${caseId}/notes`, { content: note.trim() }))) setNote(''); }} className="flex gap-2.5 px-4 py-3.5 border-b" style={{ borderColor: 'var(--surf-line)' }}>
              <span className="w-8 h-8 shrink-0 rounded-full border flex items-center justify-center text-[11px] font-semibold" style={{ background: 'var(--surf-raised)', borderColor: 'var(--surf-line)' }}>{initials(nameOf(me), me)}</span>
              <div className="flex-grow flex flex-col gap-2">
                <label htmlFor="note" className="sr-only">Add a note</label>
                <textarea id="note" rows={2} value={note} onChange={e => setNote(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) e.currentTarget.form.requestSubmit(); }} placeholder="What did you check, find or do?" className="box-border px-3 py-2.5 rounded-[10px] border text-[13.5px] leading-normal resize-y outline-none focus:border-[color:var(--accent)]" style={fieldStyle} />
                <span className="flex justify-end"><button type="submit" disabled={busy || !note.trim()} className="h-9 px-3.5 rounded-[9px] text-[13px] font-semibold disabled:opacity-50" style={primaryBtnStyle}>Post note</button></span>
              </div>
            </form>
            <div className="flex flex-col px-4 pt-3.5 pb-1">
              {!feed.length && <span className="pb-3 text-[13px]" style={{ color: 'var(--ink-faint)' }}>Nothing yet.</span>}
              {feed.map((e, i) => {
                const mine = e.kind === 'note' && String(e.actor).toLowerCase() === me.toLowerCase();
                const isEditing = editing?.id === e.id && e.kind === 'note';
                return (
                  <div key={`${e.kind}-${e.id}`} className="grid grid-cols-[64px_14px_minmax(0,1fr)] gap-2.5">
                    <span className="font-mono text-[11px] leading-5" style={{ color: 'var(--ink-faint)' }}>{stamp(e.created_at).replace('today ', '')}</span>
                    <span className="flex flex-col items-center"><span className="w-2 h-2 mt-1.5 shrink-0 rounded-full" style={{ background: dot(e) }} />{i < feed.length - 1 && <span className="flex-grow w-px" style={{ background: 'var(--surf-line)' }} />}</span>
                    <div className="pb-3.5 min-w-0">
                      {e.kind === 'note' ? (
                        <div className="flex flex-col gap-[5px] px-3 py-2.5 rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
                          <span className="flex items-center justify-between gap-2"><span className="text-[12.5px] font-semibold">{nameOf(e.actor)}</span>
                            {mine && !isEditing && !closed && (
                              <span className="flex gap-2 text-xs">
                                <button type="button" onClick={() => setEditing({ id: e.id, text: e.detail })} style={{ color: 'var(--ink-link)' }}>Edit</button>
                                <button type="button" onClick={() => act(() => apiClient.delete(`/api/cases/${caseId}/notes/${e.id}`), 'Note deleted.')} style={{ color: 'var(--ink-faint)' }}>Delete</button>
                              </span>
                            )}
                          </span>
                          {isEditing ? (
                            <form onSubmit={async ev => { ev.preventDefault(); if (await act(() => apiClient.patch(`/api/cases/${caseId}/notes/${e.id}`, { content: editing.text }))) setEditing(null); }} className="flex flex-col gap-2">
                              <label htmlFor={`edit-${e.id}`} className="sr-only">Edit note</label>
                              <textarea id={`edit-${e.id}`} autoFocus rows={3} value={editing.text} onChange={x => setEditing({ ...editing, text: x.target.value })} className="box-border px-2.5 py-2 rounded-lg border text-[13px] resize-y outline-none" style={fieldStyle} />
                              <span className="flex gap-2"><button type="submit" disabled={!editing.text.trim()} className="h-8 px-3 rounded-lg text-[12.5px] font-semibold disabled:opacity-50" style={primaryBtnStyle}>Save</button><button type="button" onClick={() => setEditing(null)} className="h-8 px-2 text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>Cancel</button></span>
                            </form>
                          ) : <span className="text-[13px] leading-relaxed whitespace-pre-wrap break-words" style={{ color: 'var(--ink-muted)' }}>{e.detail}</span>}
                        </div>
                      ) : (
                        <span className="text-[13px] leading-5 break-words" style={{ color: 'var(--ink-muted)' }}><span className="font-medium" style={{ color: 'var(--ink-text)' }}>{nameOf(e.actor)}</span> · {e.detail}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        <div className="flex flex-col gap-[18px] min-w-0">
          {/* Details */}
          <Card className="flex flex-col gap-3 px-[18px] py-4">
            <H2>Details</H2>
            <div className="grid grid-cols-2 gap-2.5">
              <label className="flex flex-col gap-[5px] text-xs" style={{ color: 'var(--ink-faint)' }}>Status
                {closed ? <span className="h-10 flex items-center px-2 text-[13px]" style={{ color: 'var(--ink-text)' }}>Closed</span> : (
                  <select value={kase.status} disabled={busy} onChange={e => patch({ status: e.target.value }, `Moved to ${STATUS_LABEL[e.target.value]}.`)} className="h-10 box-border px-2 rounded-[9px] border text-[13px]" style={fieldStyle}>
                    {['open', 'investigating', 'remediation'].map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                )}
              </label>
              <label className="flex flex-col gap-[5px] text-xs" style={{ color: 'var(--ink-faint)' }}>Priority
                <select value={kase.priority} disabled={busy || closed} onChange={e => patch({ priority: e.target.value })} className="h-10 box-border px-2 rounded-[9px] border text-[13px]" style={fieldStyle}>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
                </select>
              </label>
              <label className="col-span-2 flex flex-col gap-[5px] text-xs" style={{ color: 'var(--ink-faint)' }}>Owner
                <select value={kase.assigned_to || ''} disabled={busy || closed} onChange={e => act(() => apiClient.post(`/api/cases/${caseId}/assign`, { assigned_to: e.target.value || null }))} className="h-10 box-border px-2 rounded-[9px] border text-[13px]" style={fieldStyle}>
                  <option value="">Unassigned</option>
                  {kase.assigned_to && !members.some(m => m.email?.toLowerCase() === kase.assigned_to.toLowerCase()) && <option value={kase.assigned_to}>{kase.assigned_to} (not a member)</option>}
                  {members.map(m => <option key={m.email} value={m.email}>{m.full_name || m.email}{m.email?.toLowerCase() === me.toLowerCase() ? ' (you)' : ''}</option>)}
                </select>
              </label>
            </div>
            {!closed && <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>To close it, use Close case. It asks for an outcome and a summary.</span>}
            {kase.description && <p className="m-0 text-[13px] leading-normal whitespace-pre-wrap" style={{ color: 'var(--ink-muted)' }}>{kase.description}</p>}
            <div className="flex flex-col gap-1 pt-2.5 border-t" style={{ borderColor: 'var(--surf-line)' }}>
              {facts.map(([k, v]) => <span key={k} className="grid grid-cols-[96px_minmax(0,1fr)] gap-2.5 text-[12.5px] leading-relaxed"><span style={{ color: 'var(--ink-faint)' }}>{k}</span><span className="break-words" style={{ color: 'var(--ink-muted)' }}>{v}</span></span>)}
            </div>
          </Card>

          {/* Affected assets */}
          <Card className="flex flex-col gap-2 px-[18px] py-4">
            <H2>Affected assets</H2>
            {!(kase.hosts || []).length && <span className="text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>None yet. Assets come from the linked evidence.</span>}
            {(kase.hosts || []).map(h => {
              const r = risk[h.id];
              return (
                <Link key={h.id} to={`/assets/${h.id}`} className="flex items-center gap-2.5 px-2.5 py-2 rounded-[9px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' }}>
                  <span className="flex-grow flex flex-col gap-0.5 min-w-0"><span className="font-mono text-[12.5px] truncate">{h.hostname}</span>
                    <span className="text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{r ? `${r.active_finding_count} findings · ${r.active_ioc_count} IoC · ${r.active_cve_count} CVE` : 'asset deleted or archived'}</span></span>
                  {r && <span className="font-mono text-xs shrink-0">risk {Math.round(r.risk_score)}</span>}
                </Link>
              );
            })}
          </Card>

          {/* Response */}
          <Card className="flex flex-col gap-2.5 px-[18px] py-4">
            <H2>Response</H2>
            {(kase.runs || []).map(r => (
              <Link key={r.id} to={`/playbooks?run=${r.id}`} className="flex items-center gap-2.5 px-3 py-2.5 rounded-[9px] border" style={{ background: 'var(--surf-bg)', borderColor: r.status === 'waiting_approval' ? 'var(--sev-high-fg)' : 'var(--surf-line)', color: 'var(--ink-text)' }}>
                <span className="w-[7px] h-[7px] shrink-0 rounded-sm" style={{ background: r.status === 'waiting_approval' ? 'var(--sev-high-fg)' : r.status === 'completed' ? 'var(--sev-good-fg)' : r.status === 'failed' ? 'var(--sev-crit-fg)' : 'var(--ink-muted)' }} />
                <span className="flex-grow flex flex-col gap-0.5 min-w-0"><span className="text-[13px] font-medium truncate">RUN-{r.id.slice(0, 8).toUpperCase()} · {r.workflow_name || 'Playbook'}</span><span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{r.status === 'waiting_approval' ? 'Waiting for approval' : r.status} · {stamp(r.started_at)}</span></span>
                {r.status === 'waiting_approval' && <span className="text-[12.5px] shrink-0" style={{ color: 'var(--ink-link)' }}>Review</span>}
              </Link>
            ))}
            <span className="flex justify-between gap-3 text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>
              <span>Response actions</span>
              <span style={{ color: 'var(--ink-faint)' }}>{(kase.actions || []).length ? `${kase.actions.length} since the case opened · ${kase.actions.filter(a => a.status === 'pending').length} pending` : 'None queued yet'}</span>
            </span>
            {!closed && (
              <span className="flex flex-wrap gap-3.5 text-[12.5px]">
                <Link to={kase.hosts?.[0] ? `/playbooks?asset_id=${kase.hosts[0].id}` : '/playbooks'} style={{ color: 'var(--ink-link)' }}>{(kase.runs || []).length ? 'Run another playbook' : 'Run a playbook'}</Link>
                <Link to="/response-actions" style={{ color: 'var(--ink-link)' }}>Response Actions</Link>
              </span>
            )}
            {waitingRun && !closed && <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>Closing the case doesn’t stop a waiting run.</span>}
          </Card>

          {/* Similar */}
          {(kase.similar || []).length > 0 && (
            <Card className="flex flex-col gap-2 px-[18px] py-4">
              <H2>Similar past cases</H2>
              <span className="text-xs -mt-1" style={{ color: 'var(--ink-faint)' }}>Closed cases on the same assets</span>
              {kase.similar.map(s => (
                <Link key={s.id} to={`/cases/${s.id}`} className="flex flex-col gap-1 px-3 py-2.5 rounded-[9px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' }}>
                  <span className="text-[13px] font-medium">{caseRef(s.id)} · {s.title}</span>
                  <span className="text-xs leading-normal" style={{ color: 'var(--ink-muted)' }}>Closed {stamp(s.closed_at)}{s.resolution_outcome ? `, ${OUTCOME_LABEL[s.resolution_outcome].toLowerCase()}` : ''}.{s.resolution_summary ? ` ${s.resolution_summary}` : ''}</span>
                </Link>
              ))}
            </Card>
          )}
        </div>
      </div>

      {closing && <CloseCaseDialog kase={kase} onClose={() => setClosing(false)} onClosed={() => { setClosing(false); setNotice({ kind: 'ok', text: 'Case closed.' }); load(); }} />}
    </div>
  );
}
